import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SEO, generateOrganizationSchema, generateServiceSchema, generateBreadcrumbSchema, generateWebPageSchema } from '../../utils/seo';

export default function BusinessPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const location = useLocation();

  useEffect(() => {
    // ページ読み込み時に最上部にスクロール
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // ハッシュがある場合の処理
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      id: 1,
      category: 'print',
      title: '名刺印刷サービス',
      description: 'ビジネスの第一印象を決める名刺を、高品質な印刷技術で美しく仕上げます。豊富なデザインテンプレートから選択可能で、最短翌日納品にも対応しています。',
      price: '¥3,500〜',
      duration: '2-3営業日',
      image: 'https://public.readdy.ai/ai/img_res/5fe6700b52437d3d67df9dc02797d222.jpg',
      features: ['高品質印刷', '豊富なデザイン', '最短翌日納品', '小ロット対応']
    },
    {
      id: 2,
      category: 'print',
      title: 'チラシ・フライヤー印刷',
      description: '販促活動に欠かせないチラシやフライヤーを、鮮やかな色彩で印刷します。A4からA3まで各種サイズに対応し、大量印刷も承ります。',
      price: '¥8,000〜',
      duration: '3-5営業日',
      image: 'https://readdy.ai/api/search-image?query=colorful%20promotional%20flyers%20and%20brochures%20spread%20on%20white%20surface%20vibrant%20marketing%20materials%20with%20professional%20design%20high%20quality%20commercial%20printing%20realistic%20photography%20bright%20lighting&width=400&height=300&seq=flyer1&orientation=landscape',
      features: ['鮮やかな色彩', '各種サイズ対応', '大量印刷可能', 'コート紙・マット紙選択可']
    },
    {
      id: 3,
      category: 'print',
      title: 'ポスター印刷',
      description: '店舗やイベントでの告知に最適な大判ポスターを制作します。耐久性の高い用紙を使用し、屋内外での使用に対応しています。',
      price: '¥5,000〜',
      duration: '3-4営業日',
      image: 'https://readdy.ai/api/search-image?query=large%20format%20poster%20printing%20professional%20exhibition%20display%20colorful%20promotional%20posters%20on%20wall%20modern%20printing%20facility%20high%20quality%20commercial%20print%20realistic%20photography&width=400&height=300&seq=poster1&orientation=landscape',
      features: ['大判印刷対応', '耐久性の高い用紙', '屋内外使用可能', 'ラミネート加工可']
    },
    {
      id: 4,
      category: 'print',
      title: 'カタログ・冊子印刷',
      description: '商品カタログや会社案内などの冊子を、製本まで一貫して対応します。中綺じ・無線綺じなど、用途に応じた製本方法を選択できます。',
      price: '¥15,000〜',
      duration: '5-7営業日',
      image: 'https://readdy.ai/api/search-image?query=professional%20product%20catalogs%20and%20brochures%20on%20desk%20high%20quality%20bound%20booklets%20with%20glossy%20covers%20corporate%20marketing%20materials%20realistic%20photography%20natural%20office%20lighting&width=400&height=300&seq=catalog1&orientation=landscape',
      features: ['製本まで一貫対応', '中綺じ・無線綺じ対応', '高品質な仕上がり', 'ページ数自由設定']
    },
    {
      id: 5,
      category: 'consulting',
      title: '経営戦略コンサルティング',
      description: '企業の成長戦略立案から実行支援まで、経験豊富なコンサルタントが伴走します。市場分析、競合分析を通じて最適な戦略を提案します。',
      price: '¥300,000〜/月',
      duration: '3ヶ月〜',
      image: 'https://readdy.ai/api/search-image?query=business%20strategy%20consulting%20meeting%20japanese%20executives%20discussing%20plans%20around%20conference%20table%20with%20charts%20and%20documents%20professional%20corporate%20environment%20realistic%20photography%20natural%20lighting&width=400&height=300&seq=strategy1&orientation=landscape',
      features: ['市場分析', '競合分析', '戦略立案', '実行支援']
    },
    {
      id: 7,
      category: 'consulting',
      title: 'WEB制作サービス',
      description: '企業サイトからECサイトまで、目的に応じた最適なWebサイトを制作します。レスポンシブデザイン対応で、スマートフォンからPCまであらゆるデバイスで快適に閲覧できます。',
      price: '¥500,000〜',
      duration: '2-3ヶ月',
      image: 'https://readdy.ai/api/search-image?query=modern%20web%20design%20development%20workspace%20designer%20working%20on%20responsive%20website%20layout%20multiple%20screens%20showing%20mobile%20and%20desktop%20views%20clean%20minimalist%20office%20environment%20professional%20web%20development%20realistic%20photography%20bright%20natural%20lighting&width=400&height=300&seq=webdesign1&orientation=landscape',
      features: ['レスポンシブデザイン', 'SEO対策', 'CMS導入', '保守サポート']
    },
    {
      id: 8,
      category: 'consulting',
      title: '人材育成コンサルティング',
      description: '企業の成長を支える人材育成プログラムを設計・実施します。リーダーシップ研修からスキルアップ研修まで幅広く対応します。',
      price: '¥200,000〜/月',
      duration: '3ヶ月〜',
      image: 'https://readdy.ai/api/search-image?query=corporate%20training%20session%20professional%20instructor%20teaching%20business%20skills%20to%20employees%20in%20modern%20training%20room%20realistic%20photography%20natural%20lighting&width=400&height=300&seq=training1&orientation=landscape',
      features: ['研修プログラム設計', 'リーダーシップ研修', 'スキルアップ研修', '効果測定']
    },
    {
      id: 9,
      category: 'marketing',
      title: 'SEO対策サービス',
      description: '検索エンジンでの上位表示を実現するSEO対策を提供します。キーワード選定から内部対策、外部対策まで総合的にサポートします。',
      price: '¥150,000〜/月',
      duration: '6ヶ月〜',
      image: 'https://readdy.ai/api/search-image?query=seo%20analytics%20dashboard%20on%20computer%20screen%20showing%20search%20rankings%20and%20traffic%20data%20digital%20marketing%20workspace%20realistic%20photography%20modern%20office&width=400&height=300&seq=seo1&orientation=landscape',
      features: ['キーワード選定', '内部対策', '外部対策', 'レポート提供']
    },
    {
      id: 10,
      category: 'marketing',
      title: 'SNSマーケティング',
      description: 'Instagram、Facebook、TwitterなどのSNSを活用したマーケティング戦略を立案・実行します。エンゲージメント向上を実現します。',
      price: '¥120,000〜/月',
      duration: '3ヶ月〜',
      image: 'https://readdy.ai/api/search-image?query=social%20media%20marketing%20workspace%20with%20smartphone%20showing%20instagram%20facebook%20twitter%20apps%20analytics%20charts%20on%20laptop%20screen%20modern%20digital%20marketing%20office%20realistic%20photography&width=400&height=300&seq=sns1&orientation=landscape',
      features: ['SNS戦略立案', 'コンテンツ制作', '投稿管理', '効果分析']
    },
    {
      id: 11,
      category: 'marketing',
      title: 'Web広告運用',
      description: 'Google広告、Facebook広告などのWeb広告を効果的に運用します。ROI最大化を目指した広告戦略を提案します。',
      price: '¥100,000〜/月',
      duration: '3ヶ月〜',
      image: 'https://readdy.ai/api/search-image?query=digital%20advertising%20dashboard%20showing%20google%20ads%20facebook%20ads%20campaign%20performance%20metrics%20on%20multiple%20screens%20modern%20marketing%20office%20realistic%20photography&width=400&height=300&seq=webads1&orientation=landscape',
      features: ['広告戦略立案', 'キャンペーン設計', '運用最適化', 'ROI分析']
    },
    {
      id: 12,
      category: 'marketing',
      title: 'コンテンツマーケティング',
      description: '質の高いコンテンツ制作を通じて、顧客との関係構築を支援します。ブログ記事、ホワイトペーパー、動画など多様なコンテンツに対応します。',
      price: '¥180,000〜/月',
      duration: '6ヶ月〜',
      image: 'https://readdy.ai/api/search-image?query=content%20marketing%20creation%20workspace%20writer%20working%20on%20laptop%20with%20coffee%20creative%20office%20environment%20blog%20articles%20and%20marketing%20materials%20realistic%20photography%20natural%20lighting&width=400&height=300&seq=content1&orientation=landscape',
      features: ['コンテンツ戦略立案', '記事制作', '動画制作', '効果測定']
    },
    {
      id: 13,
      category: 'promotion',
      title: 'イベント企画・運営',
      description: '展示会、セミナー、プロモーションイベントなどの企画から運営まで一貫してサポートします。集客から当日運営まで安心してお任せください。',
      price: '¥500,000〜',
      duration: '2-3ヶ月',
      image: 'https://readdy.ai/api/search-image?query=corporate%20event%20exhibition%20booth%20with%20professional%20setup%20promotional%20displays%20attendees%20networking%20modern%20conference%20venue%20realistic%20photography%20bright%20lighting&width=400&height=300&seq=event1&orientation=landscape',
      features: ['企画立案', '会場手配', '集客支援', '当日運営']
    },
    {
      id: 14,
      category: 'promotion',
      title: 'ノベルティ制作',
      description: '企業ロゴ入りのオリジナルノベルティを制作します。ボールペン、トートバッグ、タンブラーなど、幅広いアイテムに対応しています。',
      price: '¥50,000〜',
      duration: '3-4週間',
      image: 'https://readdy.ai/api/search-image?query=corporate%20promotional%20merchandise%20branded%20items%20including%20pens%20tote%20bags%20tumblers%20with%20company%20logos%20on%20white%20background%20professional%20product%20photography&width=400&height=300&seq=novelty1&orientation=landscape',
      features: ['オリジナルデザイン', '小ロット対応', '短納期可能', '豊富なアイテム']
    },
    {
      id: 15,
      category: 'promotion',
      title: 'キャンペーン企画',
      description: '販売促進キャンペーンの企画から実施まで総合的にサポートします。効果的なプロモーション戦略で売上向上を実現します。',
      price: '¥300,000〜',
      duration: '2-3ヶ月',
      image: 'https://readdy.ai/api/search-image?query=marketing%20campaign%20planning%20meeting%20team%20discussing%20promotional%20strategy%20with%20colorful%20charts%20and%20creative%20materials%20on%20table%20modern%20office%20realistic%20photography&width=400&height=300&seq=campaign1&orientation=landscape',
      features: ['キャンペーン企画', 'クリエイティブ制作', '実施サポート', '効果測定']
    },
    {
      id: 16,
      category: 'promotion',
      title: 'サンプリング・配布',
      description: '商品サンプルやチラシの配布を代行します。ターゲットエリアでの効果的な配布により、認知度向上をサポートします。',
      price: '¥80,000〜',
      duration: '1-2週間',
      image: 'https://readdy.ai/api/search-image?query=promotional%20sampling%20distribution%20staff%20handing%20out%20product%20samples%20and%20flyers%20to%20people%20in%20urban%20area%20professional%20marketing%20activity%20realistic%20photography&width=400&height=300&seq=sampling1&orientation=landscape',
      features: ['エリア選定', '配布スタッフ手配', '配布管理', '効果レポート']
    }
  ];

  const categories = [
    { id: 'all', name: 'すべて', icon: 'ri-apps-line' },
    { id: 'print', name: 'プリントサービス', icon: 'ri-printer-line' },
    { id: 'consulting', name: 'コンサルティング', icon: 'ri-lightbulb-line' },
    { id: 'marketing', name: 'デジタルマーケティング', icon: 'ri-line-chart-line' },
    { id: 'promotion', name: 'プロモーション', icon: 'ri-megaphone-line' }
  ];

  const filteredServices = activeTab === 'all' 
    ? services.filter(service => service.id !== 8)
    : services.filter(service => service.category === activeTab && service.id !== 8);

  // Schema.org structured data
  const serviceSchemaData = services.filter(s => s.id !== 8).map(service => ({
    name: service.title,
    description: service.description,
    price: service.price
  }));

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      generateOrganizationSchema(),
      generateServiceSchema(serviceSchemaData),
      generateBreadcrumbSchema([
        { name: 'ホーム', url: siteUrl },
        { name: '事業内容', url: `${siteUrl}/business` }
      ]),
      generateWebPageSchema(
        '事業内容・サービス | 株式会社ツーサム',
        '株式会社ツーサムの事業内容とサービス一覧。プリントサービス、ビジネスコンサルティング、デジタルマーケティング、プロモーション事業など、幅広いビジネスソリューションを提供しています。',
        `${siteUrl}/business`
      )
    ]
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // バリデーション
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5sc00fc87p5i2kb2jr0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFooterLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="事業内容・サービス | 株式会社ツーサム 東京都北区"
        description="株式会社ツーサムの事業内容とサービス一覧。プリントサービス、ビジネスコンサルティング、デジタルマーケティング、プロモーション事業など、幅広いビジネスソリューションを提供。名刺印刷、チラシ制作、SEO対策、Web広告運用、イベント企画など多様なサービスで企業の成長を支援します。"
        keywords="事業内容,サービス,プリントサービス,名刺印刷,チラシ印刷,ビジネスコンサルティング,経営戦略,デジタルマーケティング,SEO対策,Web広告,プロモーション,イベント企画,東京都北区"
        canonical={`${siteUrl}/business`}
        ogType="website"
        schema={combinedSchema}
      />
      
      {/* ナビゲーション */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" onClick={handleLogoClick} className="flex items-center space-x-2 transform transition-transform duration-300 hover:scale-105">
              <div className="bg-transparent rounded-lg p-2 sm:p-3">
                <img 
                  src="https://static.readdy.ai/image/a173b05d2bea81f49058e98548b205ed/610095eea486ea38414ef21e2281d099.png" 
                  alt="株式会社ツーサム" 
                  className="h-16 sm:h-20 md:h-24 w-auto"
                />
              </div>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-gray-900 transition-all duration-300 relative group whitespace-nowrap cursor-pointer transform hover:-translate-y-0.5">
                トップページ
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gray-900 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/business" className="text-gray-900 transition-all duration-300 relative group whitespace-nowrap cursor-pointer">
                事業内容
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-900"></span>
              </Link>
              <Link to="/#contact" className="text-gray-700 hover:text-gray-900 transition-all duration-300 relative group whitespace-nowrap cursor-pointer transform hover:-translate-y-0.5">
                お問い合わせ
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gray-900 transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </div>

            {/* モバイルメニューボタン */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-gray-800 hover:text-gray-900 transition-colors duration-300 cursor-pointer"
              aria-label="メニュー"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* モバイルメニュー */}
        <div className={`md:hidden bg-white border-t border-gray-200 transition-all duration-300 overflow-hidden ${mobileMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-4 sm:px-6 py-4 space-y-4">
            <Link 
              to="/" 
              onClick={() => setMobileMenuOpen(false)}
              className="block text-gray-700 hover:text-gray-900 transition-colors duration-300 cursor-pointer py-2"
            >
              トップページ
            </Link>
            <Link 
              to="/business" 
              onClick={() => setMobileMenuOpen(false)}
              className="block text-gray-900 font-semibold transition-colors duration-300 cursor-pointer py-2"
            >
              事業内容
            </Link>
            <Link 
              to="/#contact" 
              onClick={() => setMobileMenuOpen(false)}
              className="block text-gray-700 hover:text-gray-900 transition-colors duration-300 cursor-pointer py-2"
            >
              お問い合わせ
            </Link>
          </div>
        </div>
      </nav>

      {/* ヒーローセクション */}
      <section className="pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-16 px-4 sm:px-6 bg-gradient-to-br from-teal-50 to-white" id="hero" data-animate>
        <div className="max-w-7xl mx-auto text-center">
          <div className={`transition-all duration-1000 ${visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-xs sm:text-sm text-teal-600 tracking-wider mb-3 sm:mb-4">事業内容・サービス</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif mb-4 sm:mb-6 leading-tight">
              多様なビジネスニーズに<br />
              <span className="text-teal-600">応える事業展開</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
              プリント事業からデジタル事業まで、幅広い分野でお客様のビジネスをサポートしています
            </p>
          </div>
        </div>
      </section>

      {/* 事業内容セクション */}
      <section id="business" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-white" data-animate>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-serif text-gray-800 mb-3 sm:mb-4">事業領域</h2>
            <p className="text-sm sm:text-base text-gray-600 px-4">4つの主要事業でお客様のビジネスを総合的にサポート</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            <div className={`bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${visibleSections.has('business') ? 'opacity-100 translate-y-0 delay-100' : 'opacity-0 translate-y-12'}`}>
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-printer-line text-2xl sm:text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3 sm:mb-4">プリント事業</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6">
                名刺、チラシ、ポスター、カタログなど、あらゆる印刷物の制作を承ります。高品質な印刷技術と豊富な経験で、お客様のビジネスを視覚的にサポートします。小ロットから大量印刷まで柔軟に対応し、短納期でのご提供も可能です。
              </p>
            </div>

            <div className={`bg-gradient-to-br from-green-50 to-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${visibleSections.has('business') ? 'opacity-100 translate-y-0 delay-200' : 'opacity-0 translate-y-12'}`}>
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-lightbulb-line text-2xl sm:text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3 sm:mb-4">コンサルティング事業</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6">
                経営戦略の立案から商品開発まで、企業の成長を多角的にサポートします。豊富な実績と専門知識を持つコンサルタントが、お客様のビジネス課題に寄り添い、最適なソリューションを提案します。
              </p>
            </div>

            <div className={`bg-gradient-to-br from-amber-50 to-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${visibleSections.has('business') ? 'opacity-100 translate-y-0 delay-300' : 'opacity-0 translate-y-12'}`}>
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-line-chart-line text-2xl sm:text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3 sm:mb-4">デジタルマーケティング事業</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6">
                SEO対策、SNSマーケティング、Web広告運用など、デジタル領域でのマーケティング活動を総合的にサポートします。データに基づいた戦略立案と効果測定で、確実な成果を実現します。
              </p>
            </div>

            <div className={`bg-gradient-to-br from-purple-50 to-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${visibleSections.has('business') ? 'opacity-100 translate-y-0 delay-[400ms]' : 'opacity-0 translate-y-12'}`}>
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-megaphone-line text-2xl sm:text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3 sm:mb-4">プロモーション事業</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6">
                イベント企画・運営、ノベルティ制作、キャンペーン企画など、効果的なプロモーション活動をトータルサポートします。企画から実施、効果測定まで一貫してお任せいただけます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* カテゴリータブ */}
      <section className="py-6 sm:py-8 px-4 sm:px-6 bg-gray-50" id="categories" data-animate>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-3xl sm:text-4xl font-serif text-gray-800 mb-3 sm:mb-4">サービス一覧</h2>
            <p className="text-sm sm:text-base text-gray-600 px-4">カテゴリーから絞り込んでご覧いただけます</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveTab(category.id)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer transform hover:scale-105 flex items-center gap-2 text-sm sm:text-base ${
                  activeTab === category.id
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                  <i className={`${category.icon} text-base sm:text-lg`}></i>
                </div>
                <span className="font-medium">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* サービス一覧 */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-gray-50" id="services-list" data-animate>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {filteredServices.map((service, index) => {
              return (
                <div
                  key={service.id}
                  className={`bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer group ${
                    visibleSections.has('services-list') ? `opacity-100 translate-y-0 delay-${index * 50}` : 'opacity-0 translate-y-12'
                  }`}
                  data-product-shop
                >
                  <div className="h-40 sm:h-48 md:h-56 w-full overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-4 sm:p-5 md:p-6">
                    <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-800 mb-2 group-hover:text-teal-600 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-4 sm:mb-6 line-clamp-3">
                      {service.description}
                    </p>

                    <div className="space-y-1.5 sm:space-y-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                          <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-teal-600 text-sm sm:text-base"></i>
                          </div>
                          <span className="line-clamp-1">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* お問い合わせセクション */}
      <section id="contact" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-white" data-animate>
        <div className="max-w-4xl mx-auto">
          <div className={`text-center mb-8 sm:mb-12 transition-all duration-1000 ${visibleSections.has('contact') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-xs sm:text-sm text-teal-600 tracking-wider mb-2 sm:mb-3">お問い合わせ</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif mb-3 sm:mb-4 px-4">
              お気軽にご相談ください
            </h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
              ご質問やご相談がございましたら、下記フォームよりお問い合わせください。担当者より折り返しご連絡させていただきます。
            </p>
          </div>

          <div className={`bg-gray-50 rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg transition-all duration-1000 delay-200 ${visibleSections.has('contact') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <form id="contact-form-business" data-readdy-form onSubmit={handleFormSubmit} className="space-y-5 sm:space-y-6">
              <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all duration-300 text-sm"
                  placeholder="03-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all duration-300 resize-none text-sm"
                  placeholder="お問い合わせ内容をご記入ください(500文字以内)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1 text-right">{formData.message.length}/500文字</p>
              </div>

              {formStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  お問い合わせを受け付けました。担当者より折り返しご連絡させていただきます。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  送信に失敗しました。必須項目をご確認の上、もう一度お試しください。
                </div>
              )}

              <div className="text-center pt-4">
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full sm:w-auto px-8 sm:px-12 py-3 sm:py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-300 whitespace-nowrap cursor-pointer transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base"
                >
                  {formStatus === 'submitting' ? '送信中...' : '送信する'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* フッター */}
      <footer className="bg-gray-100 text-gray-800 py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 mb-8 sm:mb-12">
            <div className="transform transition-all duration-300 hover:translate-y-[-4px]">
              <Link to="/" onClick={handleFooterLogoClick} className="flex items-center gap-2 mb-4">
                <div className="bg-white rounded-lg p-2 sm:p-3">
                  <img 
                    src="https://static.readdy.ai/image/a173b05d2bea81f49058e98548b205ed/610095eea486ea38414ef21e2281d099.png" 
                    alt="株式会社ツーサム" 
                    className="h-16 sm:h-20 md:h-24 w-auto"
                  />
                </div>
              </Link>
              <p className="text-gray-600 text-xs sm:text-sm">中小企業のデジタル変革を支援します</p>
            </div>

            <div className="transform transition-all duration-300 hover:translate-y-[-4px]">
              <h4 className="font-semibold mb-4 text-sm sm:text-base">会社情報</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-600">
                <li><Link to="/#about" className="hover:text-gray-900 transition-all duration-300 cursor-pointer hover:translate-x-1 inline-block">会社概要</Link></li>
                <li><Link to="/business" className="hover:text-gray-900 transition-all duration-300 cursor-pointer hover:translate-x-1 inline-block">事業内容</Link></li>
                <li><Link to="/#contact" className="hover:text-gray-900 transition-all duration-300 cursor-pointer hover:translate-x-1 inline-block">お問い合わせ</Link></li>
              </ul>
            </div>

            <div className="transform transition-all duration-300 hover:translate-y-[-4px]">
              <h4 className="font-semibold mb-4 text-sm sm:text-base">お問い合わせ</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-600">
                <li className="flex items-center gap-2 transition-all duration-300 hover:text-gray-900">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <span className="break-all">info@twosum.co.jp</span>
                </li>
                <li className="flex items-center gap-2 transition-all duration-300 hover:text-gray-900">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <span>03-5249-3995</span>
                </li>
                <li className="flex items-start gap-2 transition-all duration-300 hover:text-gray-900">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <span>東京都北区赤羽2-8-1 大洋堂ビル2F</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-6 sm:pt-8 border-t border-gray-300 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-xs sm:text-sm text-center sm:text-left">© 2026 株式会社ツーサム. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
